import React from 'react'
import { Field, FormikProps } from 'formik'
import { ACCOMPANYING_REPORT_FORM_INITIAL_DATA } from '../../constants'
import { TextInput } from '../../../../../../components/inputs/TextInput'
import { SelectInput } from '../../../../../../components/inputs/SelectInput'
import { MinusIcon } from '@chakra-ui/icons'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Button } from '../../../../../../components/Button'
import { SecondaryButton } from '../../../../../../components/SecondaryButton'
import { removeElement } from '../../../../../../model/helpers/arrayHelpers'
import { generateRandomString } from '../../../../../../model/helpers/stringHelpers'
import { cn } from '../../../../../../lib/utils'
import { NumberInput } from '../../../../../../components/inputs/NumberInput'

// Constants for the demolition A3 section
const DEMOLITION_A3_FIRST_POSITION_OPTIONS = [
  {
    value: '1',
    label: '1 - samostatná stavba',
  },
  {
    value: '2',
    label: '2 - soubor staveb',
  },
]

const DEMOLITION_A3_SECOND_THIRD_POSITION_OPTIONS = [
  {
    value: '0',
    label: '0 - bez vedlejší stavby',
  },
  {
    value: '1',
    label: '1 - zařízení staveniště',
  },
  {
    value: '2',
    label: '2 - pozemní stavba',
  },
  {
    value: '3',
    label: '3 - dopravní infrastruktura',
  },
  {
    value: '4',
    label: '4 - vodní dílo',
  },
  {
    value: '5',
    label: '5 - podzemní stavba',
  },
  {
    value: '6',
    label: '6 - technická infrastruktura',
  },
  {
    value: '7',
    label: '7 - úprava území',
  },
  {
    value: '8',
    label: '8 - volná řada',
  },
]

const DEMOLITION_A3_FOURTH_POSITION_OPTIONS = [
  {
    value: '1',
    label: '1 - stavba pozemní komunikace',
  },
  {
    value: '2',
    label: '2 - stavba dráhy',
  },
  {
    value: '3',
    label: '3 - letecká stavba',
  },
  {
    value: '4',
    label: '4 - ostatní stavby (nejedná se o stavbu dopr. infrastruktury)',
  },
]

interface Props {
  formik: FormikProps<any>
}

export const DemolitionAccompanyingReportA3: React.FC<Props> = ({ formik }) => {
  // Initialize the a3 structure if it doesn't exist
  React.useEffect(() => {
    if (!formik.values.a3?.demolitionStructures) {
      formik.setFieldValue('a3.demolitionStructures', [
        {
          indexIdentifier: generateRandomString(),
          firstPosition: '',
          secondPosition: '',
          thirdPosition: '0', // Default value for third position if not a secondary building
          fourthPosition: '4', // Default value for fourth position if not a transport infrastructure
          fifthPosition: '',
          name: '',
        },
      ])
    }
  }, [formik])

  return (
    <>
      <h2 id="a3Heading" className="font-semibold text-lg mt-8">
        A.3 Členění odstraňovaných staveb
      </h2>
      <div className="mt-3">
        <div className="p-3 bg-gray-50 rounded-md mb-3">
          <p className="text-sm text-gray-600 mb-3">
            Členění je tvořeno číselnou řadou, s pěti pozicemi (x.x.x.x.x) a názvem stavebního
            objektu nebo provozního souboru, kdy čísla na jednotlivých pozicích mají následující
            pevný význam:
          </p>
          <p className="text-sm text-gray-600 mb-1">
            <strong>První pozice (X.x.x.x.x.)</strong> - určuje, zda se jedná o samostatnou stavbu
            nebo soubor staveb
          </p>
          <p className="text-sm text-gray-600 mb-1">
            <strong>Druhá pozice (x.X.x.x.x)</strong> - určuje druh stavby hlavní
          </p>
          <p className="text-sm text-gray-600 mb-1">
            <strong>Třetí pozice (x.x.X.x.x)</strong> - určuje druh stavby vedlejší (v případě, že
            se nebude jednat o stavbu vedlejší, uvede se na pozici číslice „0")
          </p>
          <p className="text-sm text-gray-600 mb-1">
            <strong>Čtvrtá pozice (x.x.x.X.x)</strong> - určuje stavby dopravní infrastruktury
            (pokud se o stavbu dopravní infrastruktury nejedná uvede se na pozici číslovka „4")
          </p>
          <p className="text-sm text-gray-600 mb-3">
            <strong>Pátá pozice (x.x.x.x.X)</strong> - určuje v případě dopravních staveb číslo
            stavebního objektu nebo provozního souboru podle řady uvedené v příslušném prováděcím
            předpise Ministerstva dopravy k dopravním stavbám. V případě ostatních staveb se uvede
            číslo stavebního objektu nebo provozního souboru podle dělení projektanta.
          </p>
        </div>

        {formik.values.a3?.demolitionStructures?.map((structure: any, index: number) => (
          <div
            className={cn(
              index !== 0 ? 'mt-8' : 'mt-3',
              'border border-dotted border-gray-300 p-4 rounded-md drop-shadow-sm',
            )}
            key={structure.indexIdentifier || index}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                name={`a3.demolitionStructures[${index}].firstPosition`}
                component={SelectInput}
                options={DEMOLITION_A3_FIRST_POSITION_OPTIONS}
                label="Typ stavby (samostatná/soubor)"
                forbidCustom={true}
              />
              <Field
                name={`a3.demolitionStructures[${index}].secondPosition`}
                component={SelectInput}
                options={DEMOLITION_A3_SECOND_THIRD_POSITION_OPTIONS}
                label="Druh stavby hlavní"
                forbidCustom={true}
              />
              <Field
                name={`a3.demolitionStructures[${index}].thirdPosition`}
                component={SelectInput}
                options={DEMOLITION_A3_SECOND_THIRD_POSITION_OPTIONS}
                label="Druh stavby vedlejší"
                forbidCustom={true}
              />
              <Field
                name={`a3.demolitionStructures[${index}].fourthPosition`}
                component={SelectInput}
                options={DEMOLITION_A3_FOURTH_POSITION_OPTIONS}
                label="Typ dopravní infrastruktury"
                forbidCustom={true}
              />
              <Field
                name={`a3.demolitionStructures[${index}].fifthPosition`}
                component={NumberInput}
                min={0}
                stepper={true}
                label="Číslo stavebního objektu"
              />
            </div>
            <div className="mt-3">
              <Field
                name={`a3.demolitionStructures[${index}].name`}
                component={TextInput}
                label="Název stavebního objektu nebo provozního souboru"
              />
            </div>
            {typeof structure.firstPosition?.value !== 'undefined' &&
              typeof structure.secondPosition?.value !== 'undefined' &&
              typeof structure.thirdPosition?.value !== 'undefined' &&
              typeof structure.fourthPosition?.value !== 'undefined' &&
              typeof structure.fifthPosition !== 'undefined' && (
                <div className="mt-3 p-2 bg-gray-50 rounded-md">
                  {(() => {
                    console.log({ structure })
                    return <></>
                  })()}
                  <p className="text-sm font-medium text-gray-700">
                    Souhrn:{' '}
                    <span className="font-bold">
                      {structure.firstPosition?.value}.{structure.secondPosition?.value}.
                      {structure.thirdPosition?.value}.{structure.fourthPosition?.value}.
                      {structure.fifthPosition} {structure.name}
                    </span>
                  </p>
                </div>
              )}
            <div className="flex justify-end mt-3">
              <SecondaryButton
                onClick={() =>
                  formik.setFieldValue(
                    'a3.demolitionStructures',
                    removeElement(formik.values.a3.demolitionStructures, index),
                  )
                }
              >
                <MinusIcon className="h-5 w-5" />
              </SecondaryButton>
            </div>
          </div>
        ))}
        <div className="flex justify-end mt-3">
          <Button
            onClick={() =>
              formik.setFieldValue('a3.demolitionStructures', [
                ...(formik.values.a3?.demolitionStructures || []),
                {
                  indexIdentifier: generateRandomString(),
                  firstPosition: '',
                  secondPosition: '',
                  thirdPosition: '0', // Default value for third position if not a secondary building
                  fourthPosition: '4', // Default value for fourth position if not a transport infrastructure
                  fifthPosition: '',
                  name: '',
                },
              ])
            }
          >
            <PlusIcon className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </>
  )
}
